/**
 * Axios interceptor para manejo automático de refresh tokens
 *
 * Este interceptor:
 * 1. Intercepta respuestas con error 401 (Unauthorized)
 * 2. Intenta refrescar el token automáticamente
 * 3. Reintenta la petición original con el nuevo token
 * 4. Si el refresh falla, redirige al login
 */

import axios from 'axios';
import { refreshToken } from '../services/authService';

let isRefreshing = false;
let failedQueue = [];
let isRedirecting = false;

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

/**
 * Configura el interceptor de axios para refresh automático
 */
export const setupAxiosInterceptor = () => {
  // Interceptor de requests: Agregar token automáticamente a todas las peticiones
  axios.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token && !config.headers['Authorization']) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Interceptor de responses: Manejo automático de token refresh
  axios.interceptors.response.use(
    (response) => response, // Pasar respuestas exitosas sin cambios
    async (error) => {
      const originalRequest = error.config;

      // No interceptar peticiones de login, refresh o health check
      const isLoginRequest = originalRequest.url?.includes('/auth/login');
      const isRefreshRequest = originalRequest.url?.includes('/auth/refresh');
      const isHealthCheck = originalRequest.url?.includes('/health');

      // Si el error no es 401 o ya intentamos refrescar, rechazar
      if (error.response?.status !== 401 || originalRequest._retry || isLoginRequest || isRefreshRequest || isHealthCheck) {
        return Promise.reject(error);
      }

      // Si ya estamos refrescando, encolar esta petición
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return axios(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      // Marcar que estamos refrescando
      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Intentar refrescar el token
        const data = await refreshToken();
        const newToken = data.accessToken;

        // Guardar nuevo token
        localStorage.setItem('token', newToken);

        // Actualizar header de la petición original
        originalRequest.headers['Authorization'] = 'Bearer ' + newToken;

        // Procesar cola de peticiones pendientes
        processQueue(null, newToken);

        // Reintentar la petición original
        return axios(originalRequest);

      } catch (refreshError) {
        // El refresh falló, procesar cola con error
        processQueue(refreshError, null);

        // Solo hacer la limpieza y redirección una vez
        if (!isRedirecting) {
          isRedirecting = true;

          // Limpiar token, usuario y redirigir a login
          localStorage.removeItem('token');
          localStorage.removeItem('user');

          // Disparar evento personalizado para que AuthContext se entere
          window.dispatchEvent(new Event('session-expired'));

          // Redirigir al login si no estamos ya ahí
          if (window.location.pathname !== '/login') {
            // Pasar mensaje de sesión expirada como parámetro
            window.location.href = '/login?sessionExpired=true';
          }
        }

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }
  );
};
